import { FC, useEffect, useState } from 'react';
import {
    AnimatePresence,
    motion,
    useMotionValue,
    useSpring,
    useTransform,
} from 'framer-motion';
import classNames from 'classnames';

import { Button } from '@/Components/Button';
import { ModalView } from '@/Components/ModalView';
import { FlickAnimation } from '@/Components/FlickAnimation';
import { FruitLemonSvg } from '@/Components/Icons/FruitLemonSvg';
import { FruitBlueberrySvg } from '@/Components/Icons/FruitBlueberrySvg';
import { GlowSvg } from '@/Components/Icons/GlowSvg';
import { AppModalProps } from '@/types/modal.type';
import { FlowerSvg } from '@/Components/Icons/FlowerSvg';
import {
    INLINE_PLAYER_CLASH_ERROR_CODE,
    URL_CLASH_ERROR_CODE,
} from '@/types/api.type';
import {
    SEARCH_QUERY_ARG,
    STATUS_QUERY_ARG,
    TYPE_QUERY_ARG,
} from '@/types/query.type';
import { PUBLISHED_FLOW_STATUS } from '@/types/flow.type';

const PREPARING_STEP_ID = 'preflight-step-id/preparing';
const PUBLISHING_STEP_ID = 'preflight-step-id/publishing';
const COMPLETE_STEP_ID = 'preflight-step-id/complete';
const ERROR_STEP_ID = 'preflight-step-id/error';
const CLASH_ERROR_STEP_ID = 'preflight-step-id/clash-error';
const INLINE_CLASH_ERROR_STEP_ID = 'preflight-step-id/inline-clash-error';

type AnimationStepId =
    | typeof PREPARING_STEP_ID
    | typeof PUBLISHING_STEP_ID
    | typeof COMPLETE_STEP_ID
    | typeof ERROR_STEP_ID
    | typeof CLASH_ERROR_STEP_ID
    | typeof INLINE_CLASH_ERROR_STEP_ID;

type PreflightChecksModalProps = AppModalProps<{
    activationUrl?: string;
    preflightTask: Promise<null>;
    onError: (errors: Record<string, string[]>) => void;
    onForcePublish: () => Promise<null>;
    isSkippingAnimation?: boolean;
}>;

export const PreflightChecksModal: FC<PreflightChecksModalProps> = ({
    payload,
    onClose,
}) => {
    const {
        activationUrl,
        preflightTask,
        onError,
        onForcePublish,
        isSkippingAnimation,
    } = payload;

    const [animationStepId, setAnimationStepId] = useState<AnimationStepId>(
        isSkippingAnimation ? ERROR_STEP_ID : PREPARING_STEP_ID,
    );

    const animatedValue = useMotionValue(isSkippingAnimation ? 3 : 0);

    useEffect(() => {
        if (isSkippingAnimation) {
            return;
        }

        const timerIds: NodeJS.Timeout[] = [];

        timerIds.push(
            setTimeout(() => {
                setAnimationStepId(PUBLISHING_STEP_ID);

                preflightTask
                    .then(() => {
                        timerIds.push(
                            setTimeout(() => {
                                setAnimationStepId(COMPLETE_STEP_ID);
                            }, 250),
                        );
                    })
                    .catch((error) => {
                        timerIds.push(
                            setTimeout(() => {
                                setAnimationStepId(
                                    error?.code === URL_CLASH_ERROR_CODE
                                        ? CLASH_ERROR_STEP_ID
                                        : error?.code ===
                                            INLINE_PLAYER_CLASH_ERROR_CODE
                                          ? INLINE_CLASH_ERROR_STEP_ID
                                          : ERROR_STEP_ID,
                                );
                            }, 250),
                        );

                        onError(error);
                    });
            }, 750),
        );

        return () => {
            timerIds.forEach((timerId) => {
                clearTimeout(timerId);
            });
        };
    }, [preflightTask]);

    useEffect(() => {
        if (isSkippingAnimation) {
            return;
        }

        if (
            animationStepId === COMPLETE_STEP_ID ||
            animationStepId === ERROR_STEP_ID ||
            animationStepId === CLASH_ERROR_STEP_ID ||
            animationStepId === INLINE_CLASH_ERROR_STEP_ID
        ) {
            animatedValue.set(1);

            setTimeout(() => {
                animatedValue.set(2);
            }, 750);

            setTimeout(() => {
                animatedValue.set(3);

                // setTimeout(() => {
                //     animatedValue.set(4);
                // }, 400);
            }, 1000);
        } else if (animationStepId === PREPARING_STEP_ID) {
            animatedValue.set(0);
        }
    }, [animationStepId]);

    const onFinish = () => {
        onClose();
    };

    const onIgnoreAndTryPublishAnyway = () => {
        setAnimationStepId(PREPARING_STEP_ID);

        setTimeout(() => {
            setAnimationStepId(PUBLISHING_STEP_ID);

            onForcePublish()
                .then(() => {
                    setAnimationStepId(COMPLETE_STEP_ID);
                })
                .catch((error) => {
                    setAnimationStepId(ERROR_STEP_ID);

                    onError(error);
                });
        }, 750);
    };

    const FruitSvg =
        animationStepId === COMPLETE_STEP_ID
            ? FruitBlueberrySvg
            : FruitLemonSvg;

    const headingTextMap: Record<AnimationStepId, string> = {
        [PREPARING_STEP_ID]: 'Preparing...',
        [PUBLISHING_STEP_ID]: 'Publishing...',
        [COMPLETE_STEP_ID]: 'Published!',
        [ERROR_STEP_ID]: 'We can’t publish quite yet',
        [CLASH_ERROR_STEP_ID]: 'Overlay Player clash detected',
        [INLINE_CLASH_ERROR_STEP_ID]: 'Inline Player clash detected',
    };

    const FLICK_SIZE = 85;
    const FRUIT_SIZE = animationStepId === COMPLETE_STEP_ID ? 44 : 66;

    const flickXValue = useTransform(
        animatedValue,
        [0, 1, 2],
        [FLICK_SIZE / 4, -10, 3],
    );

    const flickYValue = useTransform(animatedValue, [0, 1, 2], [0, -22, 0]);

    const fruitXValue = useTransform(
        animatedValue,
        [0, 1],
        [FRUIT_SIZE, -FRUIT_SIZE / 3],
    );

    const fruitOpacityValue = useTransform(animatedValue, [0, 1], [0, 1]);
    const glowOpacityValue = useTransform(animatedValue, [2, 3], [0, 1]);
    const rippleOpacityValue = useTransform(animatedValue, [2, 3], [1, 0]);
    const rippleScaleValue = useTransform(animatedValue, [2, 3], [0, 30]);
    const actionPanelOpacityValue = useTransform(animatedValue, [2, 3], [0, 1]);
    const containerYValueDefault = useTransform(animatedValue, [2, 3], [34, 0]);
    const containerYValueClash = useTransform(animatedValue, [2, 3], [80, 0]);

    const flickX = useSpring(flickXValue, {
        stiffness: 600,
        damping: 20,
    });
    const flickY = useSpring(flickYValue, {
        stiffness: 600,
        damping: 20,
    });
    const fruitX = useSpring(fruitXValue);
    const fruitOpacity = useSpring(fruitOpacityValue);
    const glowOpacity = useSpring(glowOpacityValue);
    const rippleOpacity = useSpring(rippleOpacityValue);
    const rippleScale = useSpring(rippleScaleValue);
    const actionPanelOpacity = useSpring(actionPanelOpacityValue);
    const containerYDefault = useSpring(containerYValueDefault);
    const containerYClash = useSpring(containerYValueClash);

    return (
        <ModalView
            maxWidth="sm"
            onClose={onClose}
            onSubmit={onFinish}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex flex-col justify-center items-center">
            <motion.div
                style={{
                    y:
                        animationStepId === CLASH_ERROR_STEP_ID ||
                        animationStepId === INLINE_CLASH_ERROR_STEP_ID
                            ? containerYClash
                            : containerYDefault,
                }}
                className="w-full">
                <div className="flex flex-row items-center justify-center relative">
                    <motion.div
                        style={{ x: flickX, y: flickY }}
                        className="z-20">
                        <FlickAnimation className="mb-8 -mr-1.5" />
                    </motion.div>
                    <motion.div
                        style={{ x: fruitX, opacity: fruitOpacity }}
                        className="z-10 relative">
                        <FruitSvg
                            className="z-10"
                            width={FRUIT_SIZE}
                            height={FRUIT_SIZE}
                        />
                        {animationStepId === COMPLETE_STEP_ID ||
                        animationStepId === ERROR_STEP_ID ||
                        animationStepId === CLASH_ERROR_STEP_ID ||
                        animationStepId === INLINE_CLASH_ERROR_STEP_ID ? (
                            <motion.div
                                key="ripple"
                                style={{
                                    opacity: rippleOpacity,
                                    scale: rippleScale,
                                    x: FRUIT_SIZE / 2 - 8,
                                    y: FRUIT_SIZE / 2 - 8,
                                }}
                                className={classNames(
                                    'z-30 w-4 h-4 absolute top-0 left-0 rounded-full pointer-events-none',
                                    {
                                        'bg-brand-blue/40':
                                            animationStepId ===
                                            COMPLETE_STEP_ID,
                                        'bg-brand-yellow/40':
                                            animationStepId === ERROR_STEP_ID ||
                                            animationStepId ===
                                                INLINE_CLASH_ERROR_STEP_ID ||
                                            animationStepId ===
                                                CLASH_ERROR_STEP_ID,
                                    },
                                )}
                            />
                        ) : null}
                        {animationStepId === COMPLETE_STEP_ID ? (
                            <motion.div
                                key="glow"
                                style={{
                                    opacity: glowOpacity,
                                    x: -35,
                                    y: -35,
                                }}
                                className="-z-10 absolute top-0 left-0 pointer-events-none">
                                <GlowSvg />
                            </motion.div>
                        ) : null}
                    </motion.div>
                </div>
                <div className="w-full text-center mb-1 h-7 relative">
                    <AnimatePresence>
                        {Object.keys(headingTextMap).map((stepId) => {
                            if (animationStepId !== stepId) {
                                return null;
                            }

                            return (
                                <motion.div
                                    key={stepId}
                                    initial={{
                                        opacity: 0,
                                        x:
                                            stepId === PREPARING_STEP_ID
                                                ? 0
                                                : 50,
                                    }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    className="absolute top-0 left-0 w-full h-full text-heading-four font-area-normal text-brand-ocean">
                                    {headingTextMap[stepId]}
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
                <motion.div
                    style={{
                        opacity: actionPanelOpacity,
                    }}
                    className="w-full flex flex-col items-center justify-center">
                    {animationStepId === ERROR_STEP_ID ? (
                        <div className="flex flex-row items-center text-body-text-15 text-brand-ocean">
                            <FlowerSvg
                                width="14"
                                height="14"
                                className="mr-1"
                            />
                            Please review items marked with a red dot.
                        </div>
                    ) : animationStepId === CLASH_ERROR_STEP_ID ||
                      animationStepId === INLINE_CLASH_ERROR_STEP_ID ? (
                        <div className="flex flex-row items-center text-body-text-15 text-center text-brand-ocean">
                            {animationStepId === INLINE_CLASH_ERROR_STEP_ID ? (
                                <>
                                    An existing published Inline Flow has been
                                    detected for the same page and Target
                                    Element. Publishing another Flow could
                                    result in Players clashing.
                                </>
                            ) : (
                                <>
                                    Another Overlay Flow is already Published on{' '}
                                    <br />
                                    the page. Publishing could result in Flows
                                    <br /> overlapping each other.
                                </>
                            )}
                        </div>
                    ) : null}
                    {animationStepId === CLASH_ERROR_STEP_ID ||
                    animationStepId === INLINE_CLASH_ERROR_STEP_ID ? (
                        <div className="flex flex-col items-center justify-center gap-y-2.5 w-full mt-6">
                            <Button
                                variant="primary"
                                className="w-full h-9 flex flex-row items-center justify-center gap-x-1"
                                onClick={onFinish}>
                                Stop and don’t Publish
                            </Button>
                            <Button
                                variant="default"
                                className="w-full h-9 flex flex-row items-center justify-center gap-x-1 relative"
                                target="_blank"
                                elementType="a"
                                href={route('flows.index', {
                                    [STATUS_QUERY_ARG]: PUBLISHED_FLOW_STATUS,
                                    [TYPE_QUERY_ARG]:
                                        animationStepId ===
                                        INLINE_CLASH_ERROR_STEP_ID
                                            ? 'inline'
                                            : 'overlay',
                                    [SEARCH_QUERY_ARG]: activationUrl,
                                })}
                                onClick={onClose}>
                                Review existing Published Flows
                            </Button>
                            <Button
                                variant="default"
                                className="w-full h-9 flex flex-row items-center justify-center gap-x-1 relative"
                                onClick={onIgnoreAndTryPublishAnyway}>
                                Ignore and Publish anyway
                            </Button>
                        </div>
                    ) : (
                        <Button
                            className="w-full h-9 mt-4"
                            variant={
                                animationStepId === ERROR_STEP_ID
                                    ? 'default'
                                    : 'primary'
                            }
                            onClick={onFinish}>
                            {animationStepId === ERROR_STEP_ID
                                ? 'Got it'
                                : 'Done'}
                        </Button>
                    )}
                </motion.div>
            </motion.div>
        </ModalView>
    );
};
